import { useQuery } from "@tanstack/react-query";
import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { DynamicApplicationForm, FormSchema } from "@/components/DynamicApplicationForm";
import { Link } from "wouter";
import { ArrowLeft, Loader2 } from "lucide-react";

function WhistleIcon({ size = 28, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10.83 8.17A4 4 0 0 1 14 7h7l-2 9H7l3.83-7.83z"/>
      <circle cx="5" cy="15" r="3"/>
      <path d="M7.76 12.24 10 10"/>
    </svg>
  );
}

import { COACH_SCHEMA_DEFAULT } from "@/lib/defaultFormSchemas";

export default function JoinCoach() {
  const { data: schema, isLoading } = useQuery<FormSchema>({
    queryKey: ["/api/join/schema/coach"],
    queryFn: async () => {
      try {
        const r = await fetch("/api/join/schema/coach");
        if (!r.ok) return COACH_SCHEMA_DEFAULT;
        const data = await r.json();
        if (!data || !data.sections) return COACH_SCHEMA_DEFAULT;
        return data;
      } catch (err) {
        return COACH_SCHEMA_DEFAULT;
      }
    },
    staleTime: 1000 * 60 * 5,
    initialData: COACH_SCHEMA_DEFAULT,
  });

  return (
    <>
      <SeoHead
        title="Coach Application"
        description="Apply to join Eliada Sports Agency as a coach or sports development professional. Submit your application and contribute to shaping elite athletes."
        path="/join/coach"
      />
      <PageTransition className="pt-24 pb-24 bg-[#070b13] min-h-screen">
        {/* Header */}
        <div className="relative py-16 border-b border-white/5 bg-secondary/20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-transparent opacity-70" />
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link href="/join" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-blue-400 transition-colors mb-6">
              <ArrowLeft size={14} /> Back to Pathways
            </Link>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center shrink-0">
                <WhistleIcon size={28} className="text-blue-400" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-blue-400 mb-1">Coach Application</p>
                <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-white">
                  COACH APPLICATION<br className="hidden sm:block" /> FORM
                </h1>
              </div>
            </div>
            <p className="text-white/50 text-sm mt-4 max-w-2xl leading-relaxed">
              Complete all sections below. Fields marked with <span className="text-blue-400 font-bold">*</span> are required.
              Applications are reviewed by our recruitment and athlete development team.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          {!schema || !schema.sections ? (
            <div className="text-center py-24 text-white/50">
              <p>Unable to load the application form. Please try again later.</p>
            </div>
          ) : (
            <DynamicApplicationForm schema={schema} formType="coach" />
          )}
        </div>
      </PageTransition>
    </>
  );
}
